import React, { useState } from "react";
import { message } from "antd";
import Table, { RowType } from "components/Shared/CustomTable";
import UserModal from "./UserModal";
import { ManagedUser } from "store/types";
import { useUserState } from "store/hooks";
import { useUsersDispatch } from "store/hooks/users";
import { isCC, simplifyRole } from "helpers/customHelpers";

import "./UsersTable.scss";
import ConfirmationModal from "../Modals/ConfirmationModal";
import { CONFIRMATION_MODAL_CONFIG, USER_ROLES_FILTER } from "config/constants";
import { Dropdown } from "react-bootstrap";

const UsersTable = ({
  users,
  teamId,
  showRoleFilter = false,
}: {
  users: ManagedUser[];
  teamId: string;
  showRoleFilter?: boolean;
}) => {
  const loggedUser = useUserState();
  const { deleteUser, sendWelcomeEmail } = useUsersDispatch();
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<
    "delete" | "resend"
  >("delete");
  const [sendingUserIds, setSendingUserIds] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null);
  const [applicationRoleFilter, setApplicationRoleFilter] = useState<string>(
    USER_ROLES_FILTER.default
  );

  const handleClose = () => setShowModal(false);

  const handleAddUser = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user: ManagedUser) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleDeleteUser = (user: ManagedUser) => {
    setSelectedUser(user);
    setConfirmationAction("delete");
    setShowConfirmationModal(true);
  };

  const handleResendEmail = (user: ManagedUser) => {
    setSelectedUser(user);
    setConfirmationAction("resend");
    setShowConfirmationModal(true);
  };

  const sendUserWelcomeEmail = async (user: ManagedUser) => {
    setSendingUserIds((current) =>
      current.includes(user._id) ? current : [...current, user._id]
    );

    try {
      const response = await sendWelcomeEmail(user._id);
      if (typeof response !== "string") {
        if (response.code === 0) {
          message.success(
            {
              content: response.message,
              className: "message-pro",
            },
            10
          );
        } else {
          message.error(
            {
              content: response.message,
              className: "antd-message-alert",
            },
            10
          );
        }
      }
    } finally {
      setSendingUserIds((current) =>
        current.filter((userId) => userId !== user._id)
      );
    }
  };

  const filteredUsers = users.filter((user) => {
    if (applicationRoleFilter === USER_ROLES_FILTER.default) return true;

    const hasAdminRole = user.roles.some((r) => r.role.includes("Admin"));
    const hasUserRole = user.roles.some(
      (r) => r.role.includes("Analyst") || r.role.includes("User")
    );

    if (applicationRoleFilter === "Admin") return hasAdminRole;
    if (applicationRoleFilter === "User") return hasUserRole;
    if (applicationRoleFilter === "Other") return !hasAdminRole && !hasUserRole;

    return true;
  });

  const isPartnerUser = (user: ManagedUser) =>
    user.roles.every((r) => simplifyRole(r.role) === "Partner");

  const headers = [
    {
      content: "NAME",
      class: "l-ll t-ll",
      style: { verticalAlign: "top" },
    },
    {
      content: "EMAIL ADDRESS",
      class: "t-ll",
      style: { verticalAlign: "top" },
    },
    {
      content: "APPLICATION ROLE",
      style: { width: "200px" },
      print: (content: string) => (
        <>
          {content}
          {showRoleFilter && (
            <div onClick={(event) => event.stopPropagation()}>
              <Dropdown className={"entities-dd"}>
                <Dropdown.Toggle className={"button-000"}>
                  {applicationRoleFilter}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {USER_ROLES_FILTER.types.map((type, index) => (
                    <Dropdown.Item
                      key={index}
                      onClick={() => setApplicationRoleFilter(type)}
                    >
                      {type}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </>
      ),
    },
    { style: { width: "300px" }, class: "t-ll" },
  ];

  const rows: RowType[] = filteredUsers.map((user, index) => ({
    columns: [
      { class: "l-ll t-ll", content: `${user.firstName} ${user.lastName}` },
      { class: "t-ll", content: user.userid },
      {
        content: user.roles
          .map((r) => (!isCC(loggedUser) ? simplifyRole(r.role) : r.role))
          .join(", "),
      },
      {
        style: { position: "relative", textAlign: "right" },
        class: "t-ll",
        print: () => {
          const disabled = !isCC(loggedUser) && isPartnerUser(user);
          return (
            <>
              <button
                className="btn-border"
                disabled={disabled || sendingUserIds.includes(user._id)}
                onClick={() => handleResendEmail(user)}
              >
                <i className={"mdi mdi-email-send-outline"}></i>
                Re-send
              </button>
              <button
                className="btn-blue"
                onClick={() => handleEditUser(user)}
                disabled={disabled}
              >
                Edit
              </button>
              <button
                className="btn-gray"
                onClick={() => handleDeleteUser(user)}
                disabled={disabled}
              >
                Delete
              </button>
            </>
          );
        },
      },
    ],
    id: `customers-table-${index}`,
  }));

  return (
    <div className="users-table">
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        modalConfig={CONFIRMATION_MODAL_CONFIG}
        modalContent={{
          headerContent: " ",
          bodyContent:
            confirmationAction === "delete"
              ? "Are you sure you want to delete this user?"
              : `Are you sure you want to re-send user setup instructions to ${selectedUser?.userid}?`,
          confirmContent:
            confirmationAction === "delete"
              ? "Yes, delete user"
              : "Yes, re-send email",
        }}
        confirmAction={() => {
          if (!selectedUser) return;
          if (confirmationAction === "delete") {
            deleteUser(selectedUser._id);
          } else {
            sendUserWelcomeEmail(selectedUser);
          }
        }}
      />
      <UserModal
        show={showModal}
        onClose={handleClose}
        selectedUser={selectedUser}
        teamId={teamId}
        isCommandCenter={isCC(loggedUser)}
      />
      <div className="title-b">
        Users
        <button className="btn-blue" onClick={handleAddUser}>
          Add User
        </button>
      </div>
      <Table
        classTable="users-customers-table table-with-borders"
        headers={headers}
        rows={rows}
        withoutScroll
        forceScope="USERS_TABLE"
      />
    </div>
  );
};

export default UsersTable;
